import { AuditStatus } from '@arhs-fleetback/types/entities/audits';

import { DELETE_SUCCESS, PATCH_ASSIGNEES_SUCCESS, PATCH_STATUS_SUCCESS } from 'modules/audits/actionTypes';
import { INSPECTION_TYPE_ENUM } from 'modules/interventions/constants/inspections';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { createEntityAdapter, createReducer } from 'utils/ReduxUtils';

import * as types from '../../../../actionTypes';

/**
 * Entity adapter for normalized inspections data (ids + entities).
 * @type {import('utils/ReduxUtils').EntityAdapter<import('../../../../types/inspections').Inspection>}
 */
export const inspectionsAdapter = createEntityAdapter({ selectId: ({ id }) => id });

/**
 * Handle successful fetch of inspections.
 * Replaces all existing inspections with new ones.
 */
const handleFetchInspectionsSuccess = (state, { payload }) => {
  const { inspections } = payload;
  return inspectionsAdapter.setAll(state, inspections);
};

/**
 * Handle successful import of audits or tscans.
 * Marks imported inspections with imported: true flag.
 */
const handleImportAuditsOrTscansSuccess = (state, { payload }) => {
  const { importedIds } = payload;
  const updates = importedIds
    .filter(id => state.entities[id])
    .map(id => ({
      id,
      imported: true,
    }));

  return inspectionsAdapter.updateMany(state, updates);
};

/**
 * Handle successful patch of audit status from dropdown menu.
 * Updates the status of the inspection if it exists and is an AUDIT type.
 */
const handlePatchStatusSuccess = (state, { payload: audit }) => {
  const { id: auditId, status } = audit;
  const inspection = state.entities[auditId];
  const shouldUpdate = inspection && inspection.type === INSPECTION_TYPE_ENUM.AUDIT;

  if (shouldUpdate) {
    return inspectionsAdapter.updateOne(state, {
      id: auditId,
      status,
    });
  }
  return state;
};

/**
 * Keeps the inspections list in sync with the assignee, whether it was set from the list itself,
 * from the audit drawer or from the audit details.
 */
const handlePatchAssigneesSuccess = (state, { payload: audit }) => {
  const { id: auditId, assignedUser, assignedUserGroup } = audit;
  const inspection = state.entities[auditId];
  const shouldUpdate = inspection && inspection.type === INSPECTION_TYPE_ENUM.AUDIT;

  if (shouldUpdate) {
    return inspectionsAdapter.updateOne(state, {
      id: auditId,
      assignedUser,
      assignedUserGroup,
    });
  }
  return state;
};

/**
 * Handle successful deletion of an inspection.
 */
const removeInspectionOnDelete = (state, { payload: auditId }) => {
  const inspection = state.entities[auditId];
  if (inspection?.imported) {
    return inspectionsAdapter.updateOne(state, { id: auditId, status: AuditStatus.DELETED });
  }
  return inspectionsAdapter.removeOne(state, auditId);
};

/**
 * Reset data state to initial.
 */
const resetData = () => inspectionsAdapter.getInitialState();

/**
 * Reducer for inspections data (ids + entities).
 * Handles fetching and updating inspection entities.
 */
export default createReducer(inspectionsAdapter.getInitialState(), {
  [types.FETCH_INSPECTIONS_SUCCESS]: handleFetchInspectionsSuccess,
  [types.IMPORT_AUDITS_OR_TSCANS_SUCCESS]: handleImportAuditsOrTscansSuccess,
  [PATCH_STATUS_SUCCESS]: handlePatchStatusSuccess,
  [PATCH_ASSIGNEES_SUCCESS]: handlePatchAssigneesSuccess,
  [LOCATION_CHANGE]: resetData,
  [DELETE_SUCCESS]: removeInspectionOnDelete
});
